import { useEffect, useState } from 'react'
import type { QueueCache, QueueCacheEntry, QueueCacheHealth, QueueCachePurpose } from '../api/types'
import { errorMessage } from '../api/client'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState } from '../store/context'

const GENERATIONS = 4
const ENTRIES: Record<QueueCacheEntry, string> = {
  not_directory: 'not a directory',
  completion_hint: 'invalid completion hint',
  cache_directory: 'invalid cache folder',
  unreadable: 'unreadable',
}
const gigabytes = (bytes: number) => `${(bytes / 1e9).toFixed(1)} GB`
const generations = (count: number) => `${count} of ${GENERATIONS} generations`

const PURPOSES = [['landing', 'landing'], ['idle_main', 'idle-main']] as const
/** The cached purposes whose own cache folder is invalid, as people name them. */
function fallingBack(health: QueueCacheHealth): string[] {
  return health.state === 'ready' ? PURPOSES.filter(([key]) => health.purposes[key] === 'fallback').map(([, name]) => name) : []
}

/** What the next cached queue check finds, in a person's words. */
export function cacheSummary(health: QueueCacheHealth): string {
  switch (health.state) {
    case 'unused': return 'No queue check has used a cache yet.'
    case 'in_use': return 'A check is using the cache now. Refresh once it ends.'
    case 'unreadable': return 'The runner could not read this project’s cache folder. Queue checks run uncached.'
    case 'ready': {
      const falling = fallingBack(health)
      const quarantine = health.uncertain === 0 ? '' : ` ${generations(health.uncertain)} quarantined.`
      if (falling.length === PURPOSES.length) return `Uncached: the landing and idle-main cache folders are invalid, so those checks fall back to disposable sources.${quarantine}`
      if (falling.length === 1) {
        const other = PURPOSES.find(([, name]) => name !== falling[0])?.[1]
        return `Partly uncached: ${falling[0]} checks fall back to disposable sources (invalid cache folder); ${other} checks reuse their cache.${quarantine}`
      }
      const summary = health.uncertain === 0 ? 'Healthy: queue checks reuse their cache.' : `Reusing, with ${generations(health.uncertain)} quarantined.`
      const invalid = health.retained_invalid?.length ?? 0
      return invalid ? `${summary} ${invalid} invalid generation${invalid === 1 ? ' awaits' : 's await'} safe repair or manual recovery; caching remains available.` : summary
    }
    case 'exhausted': return `Uncached: all ${GENERATIONS} generations are quarantined, so queue checks fall back to disposable sources.`
    case 'malformed': return 'Uncached: an invalid entry disables caching for the whole project.'
    case 'budget': return `Uncached: quarantined generations hold ${gigabytes(health.uncertain_bytes)}, at or over the ${gigabytes(health.budget_bytes)} budget.`
    case 'capacity': {
      const source = health.source === 'important' ? 'macOS important-work capacity' : health.source === 'immediate_fallback' ? 'immediate capacity (macOS fallback)' : 'immediate capacity'
      const reading = health.available_bytes === null ? 'capacity could not be read' : `${source} is ${gigabytes(health.available_bytes)}`
      return `New cache unavailable: ${reading}; ${gigabytes(health.required_bytes)} is required. This does not establish capacity for an uncached build.`
    }
  }
}

/** What a person must recover by hand; null when unknown. */
export function cacheRecovery(health: QueueCacheHealth): string | null {
  if (health.state === 'in_use' || health.state === 'unreadable') return null
  const quarantined = health.state === 'exhausted' ? GENERATIONS : health.state === 'unused' ? 0 : health.uncertain
  const falling = fallingBack(health)
  const steps = [
    ...health.state === 'malformed' ? ['a person repairs or removes the invalid entries by hand'] : [],
    ...falling.length ? [`a person repairs or removes the invalid ${falling.join(' and ')} cache folder${falling.length === 1 ? '' : 's'} by hand`] : [],
    ...((health.state === 'ready' || health.state === 'capacity') && health.retained_invalid?.length) ? ['invalid entries without proven idle ownership need manual recovery'] : [],
    ...quarantined ? [`${generations(quarantined)} ${quarantined === 1 ? 'waits' : 'wait'} for a person to prove the old checks stopped and reclaim ${quarantined === 1 ? 'it' : 'them'} by hand`] : [],
  ]
  if (health.state === 'capacity') steps.push('new cache allocation needs sufficient capacity; no quarantined files are reclaimed')
  return steps.length ? `Recovery: ${steps.join('; ')}. Uncertain files stay untouched.` : 'Recovery: none needed.'
}

const purpose = (state: QueueCachePurpose) => state === 'reusable' ? 'reusable' : 'falls back (invalid cache folder)'

/**
 * Each project's queue-cache health on this runner (Poulpe-561), read live from
 * its disk, so it outlasts the job that caused it. It shows no paths and grants
 * no recovery: that stays a person's, by hand.
 */
export function QueueCaches({ runner, unavailable }: { runner: string; unavailable: boolean }) {
  const { api } = useSessionsEnv()
  const { projects } = useBoardState()
  const [caches, setCaches] = useState<QueueCache[] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => {
    let active = true
    // Each read replaces the last: nothing older is shown as current.
    setCaches(null)
    setProblem(null)
    if (unavailable || !api.cacheHealth) return
    api.cacheHealth(runner).then(read => { if (active) setCaches(read) }, error => { if (active) setProblem(errorMessage(error)) })
    return () => { active = false }
  }, [runner, unavailable, api])
  if (problem) return <p role="alert" className="text-danger">{problem}</p>
  if (!caches) return null
  return <section className="settings-group" aria-label="Queue caches">
    <h3>Queue caches</h3>
    {caches.length === 0 && <p className="muted">This runner checks no project.</p>}
    {caches.map(({ project, health }) => {
      const recovery = cacheRecovery(health)
      return <div key={project} role="group" aria-label={projects.get(project)?.name ?? project}>
        <h4>{projects.get(project)?.name ?? project}</h4>
        <p>{cacheSummary(health)}</p>
        {(health.state === 'malformed' || health.state === 'ready' || health.state === 'capacity') && <ul>{(health.state === 'malformed' ? health.entries : health.retained_invalid ?? []).map(e => <li key={e.generation}>Generation {e.generation}: {ENTRIES[e.entry]}</li>)}</ul>}
        {health.state === 'malformed' && health.uncertain > 0 && <p>{generations(health.uncertain)} also quarantined.</p>}
        {health.state === 'ready' && <ul>
          <li>Landing checks: {purpose(health.purposes.landing)}</li>
          <li>Idle-main checks: {purpose(health.purposes.idle_main)}</li>
          <li>Proposal and hand checks: uncached by design</li>
        </ul>}
        {(health.state === 'ready' || health.state === 'capacity') && health.storage && <p className="muted">
          Reusable cache: {gigabytes(health.storage.reusable.logical_bytes)} logical, {gigabytes(health.storage.reusable.allocated_bytes)} allocated block sum; {gigabytes(health.storage.reusable_budget_bytes)} retention budget.
          {' '}Quarantined and invalid entries: {health.storage.quarantined ? `${gigabytes(health.storage.quarantined.logical_bytes)} logical, ${gigabytes(health.storage.quarantined.allocated_bytes)} allocated block sum` : 'size unknown'}.
          {' '}Block sums can count shared data more than once; they are not reclaimable space.
        </p>}
        {recovery && <p className="muted">{recovery}</p>}
      </div>
    })}
  </section>
}
